import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import * as Icons from "lucide-react";
import { ChevronRight } from "lucide-react";

// Dynamic Icon Component
const Ico = ({ name, ...props }) => {
  const C = Icons[name] || Icons.Circle;
  return <C {...props} />;
};

// Data: Business Functions
export const FUNCTIONS = [
  {
    key: "revenue",
    label: "Revenue & Growth",
    sub: "Find, win and grow",
    icon: "TrendingUp",
    color: "#2563EB",
    bg: "#DBEAFE",
    border: "#BFDBFE",
    headline: "Accelerate growth with intelligent insight.",
    desc: "Identify opportunities, anticipate customer needs, improve win rates and unlock new revenue streams with AI.",
    img: "obj_1",
    examples: [
      { title: "Intelligent Lead Scoring", desc: "Identify and prioritize high-value leads using AI.", icon: "Filter", color: "#7C67FC", bg: "#F2F0FE" },
      { title: "Revenue Forecasting", desc: "Predict revenue with higher accuracy using real-time data.", icon: "LineChart", color: "#059669", bg: "#D8FFEB" },
      { title: "Sales Enablement Agent", desc: "Give your sales team an always-on AI assistant.", icon: "Bot", color: "#FB8B53", bg: "#FFEFDE" },
      { title: "Market Expansion Insights", desc: "Discover new markets and segments using AI.", icon: "Globe", color: "#9333EA", bg: "#F3E8FF" },
    ],
  },
  {
    key: "cx",
    label: "Customer Experience",
    sub: "Delight at every touchpoint",
    icon: "Heart",
    color: "#16A34A",
    bg: "#F0FDF4",
    border: "#BBF7D0",
    headline: "Delight customers at every touchpoint.",
    desc: "Personalize journeys, resolve issues faster and anticipate needs with AI that understands your customers.",
    img: "obj_2",
    examples: [
      { title: "AI Support Copilot", desc: "Resolve customer queries instantly, around the clock.", icon: "Bot", color: "#7C67FC", bg: "#F2F0FE" },
      { title: "Personalized Journeys", desc: "Tailor every interaction to each customer.", icon: "Sparkles", color: "#059669", bg: "#D8FFEB" },
      { title: "Sentiment Analysis", desc: "Understand how customers feel in real time.", icon: "Smile", color: "#FB8B53", bg: "#FFEFDE" },
      { title: "Churn Prediction", desc: "Spot at-risk customers before they leave.", icon: "AlertTriangle", color: "#9333EA", bg: "#F3E8FF" },
    ],
  },
  {
    key: "ops",
    label: "Operations",
    sub: "Do more with less",
    icon: "Settings",
    color: "#EA580C",
    bg: "#FFF7ED",
    border: "#FED7AA",
    headline: "Run leaner, smarter operations.",
    desc: "Automate workflows, predict bottlenecks and optimize resources so your teams can do more with less.",
    img: "obj_3",
    examples: [
      { title: "Process Automation", desc: "Automate repetitive tasks end to end.", icon: "Workflow", color: "#7C67FC", bg: "#F2F0FE" },
      { title: "Demand Forecasting", desc: "Plan capacity with predictive accuracy.", icon: "LineChart", color: "#059669", bg: "#D8FFEB" },
      { title: "Predictive Maintenance", desc: "Fix issues before they cause downtime.", icon: "Wrench", color: "#FB8B53", bg: "#FFEFDE" },
      { title: "Supply Chain Insights", desc: "Optimize inventory and logistics with AI.", icon: "Truck", color: "#9333EA", bg: "#F3E8FF" },
    ],
  },
  {
    key: "finance",
    label: "Finance",
    sub: "Improve profitability",
    icon: "DollarSign",
    color: "#D97706",
    bg: "#FFFBEB",
    border: "#FDE68A",
    headline: "Sharpen every financial decision.",
    desc: "Close faster, detect risk earlier and forecast with confidence using AI across your finance function.",
    img: "obj_4",
    examples: [
      { title: "Fraud Detection", desc: "Catch anomalies and fraud in real time.", icon: "ShieldCheck", color: "#7C67FC", bg: "#F2F0FE" },
      { title: "Automated Reconciliation", desc: "Match transactions automatically at scale.", icon: "CheckCheck", color: "#059669", bg: "#D8FFEB" },
      { title: "Cash Flow Forecasting", desc: "Anticipate cash needs with precision.", icon: "LineChart", color: "#FB8B53", bg: "#FFEFDE" },
      { title: "Spend Intelligence", desc: "Find savings across your spend with AI.", icon: "PiggyBank", color: "#9333EA", bg: "#F3E8FF" },
    ],
  },
  {
    key: "people",
    label: "People",
    sub: "Empower talent",
    icon: "Users",
    color: "#9333EA",
    bg: "#FDF4FF",
    border: "#F5D0FE",
    headline: "Empower your people with AI.",
    desc: "Hire smarter, upskill faster and free your teams to focus on the work that matters most.",
    img: "obj_5",
    examples: [
      { title: "Smart Recruiting", desc: "Screen and match candidates with AI.", icon: "UserPlus", color: "#7C67FC", bg: "#F2F0FE" },
      { title: "Learning Copilot", desc: "Personalized upskilling for every employee.", icon: "GraduationCap", color: "#059669", bg: "#D8FFEB" },
      { title: "HR Knowledge Agent", desc: "Instant answers to every people question.", icon: "Bot", color: "#FB8B53", bg: "#FFEFDE" },
      { title: "Retention Insights", desc: "Understand and improve employee engagement.", icon: "HeartHandshake", color: "#9333EA", bg: "#F3E8FF" },
    ],
  },
  {
    key: "knowledge",
    label: "Knowledge & Decisions",
    sub: "Turn insight into action",
    icon: "BookOpen",
    color: "#2563EB",
    bg: "#EFF6FF",
    border: "#BFDBFE",
    headline: "Turn knowledge into better decisions.",
    desc: "Surface the right information instantly and make faster, smarter decisions with AI you can trust.",
    img: "obj_6",
    examples: [
      { title: "Enterprise Search", desc: "Find any answer across your knowledge base.", icon: "Search", color: "#7C67FC", bg: "#F2F0FE" },
      { title: "Decision Copilot", desc: "AI recommendations grounded in your data.", icon: "Lightbulb", color: "#059669", bg: "#D8FFEB" },
      { title: "Document Intelligence", desc: "Extract insight from any document instantly.", icon: "FileText", color: "#FB8B53", bg: "#FFEFDE" },
      { title: "Insight Dashboards", desc: "Real-time insight for every team.", icon: "BarChart3", color: "#9333EA", bg: "#F3E8FF" },
    ],
  },
  {
    key: "technology",
    label: "Technology",
    sub: "Modernize with purpose",
    icon: "Cpu",
    color: "#0EA5E9",
    bg: "#F0F9FF",
    border: "#BAE6FD",
    headline: "Modernize your technology with purpose.",
    desc: "Ship faster, reduce technical debt and build AI-native systems ready for what's next.",
    img: "obj_7",
    examples: [
      { title: "Code Copilot", desc: "Accelerate development with AI pair programming.", icon: "Code", color: "#7C67FC", bg: "#F2F0FE" },
      { title: "Legacy Modernization", desc: "Refactor and migrate systems with AI.", icon: "RefreshCw", color: "#059669", bg: "#D8FFEB" },
      { title: "AIOps & Monitoring", desc: "Detect and resolve incidents proactively.", icon: "Activity", color: "#FB8B53", bg: "#FFEFDE" },
      { title: "Private AI Platform", desc: "Deploy secure, enterprise-grade AI.", icon: "ServerCog", color: "#9333EA", bg: "#F3E8FF" },
    ],
  },
];

// Data: Business Objectives
export const OBJECTIVES = [
  {
    key: "revenue",
    label: "Grow Revenue",
    sub: "Find new growth",
    icon: "TrendingUp",
    color: "#2563EB",
    bg: "#EFF6FF",
    border: "#BFDBFE",
    headline: "Turn AI into a growth engine.",
    desc: "Identify new revenue opportunities, improve conversion, understand customer intent and help your teams make smarter growth decisions with AI.",
    img: "obj_1",
    examples: [
      { title: "Opportunity Intelligence", desc: "Identify new revenue opportunities across customers, markets and products.", icon: "Target", color: "#7C67FC", bg: "#F3E8FF" },
      { title: "Conversion Intelligence", desc: "Understand what drives conversion and help teams focus on the highest-value opportunities.", icon: "Lightbulb", color: "#10B981", bg: "#DCFCE7" },
      { title: "Next Best Opportunity", desc: "Help sales teams identify which customers, products or opportunities to pursue next.", icon: "Users", color: "#F97316", bg: "#FFEDD5" },
      { title: "New Market Discovery", desc: "Use AI to identify emerging segments, unmet needs and potential markets.", icon: "Search", color: "#A855F7", bg: "#F3E8FF" },
    ],
  },
  {
    key: "cx",
    label: "Improve Customer Experience",
    sub: "Delight every customer",
    icon: "Users",
    color: "#16A34A",
    bg: "#F0FDF4",
    border: "#BBF7D0",
    headline: "Make every customer interaction more intelligent.",
    desc: "Understand customer needs, anticipate intent and deliver more relevant experiences across every touchpoint.",
    img: "obj_2",
    examples: [
      { title: "Customer Journey Intelligence", desc: "Understand customer journeys and identify moments where experience can improve.", icon: "TrendingUp", color: "#7C67FC", bg: "#F2F0FE" },
      { title: "Intent Intelligence", desc: "Understand what customers are trying to achieve from conversations, behavior and interactions.", icon: "Lightbulb", color: "#059669", bg: "#D8FFEB" },
      { title: "Next Best Action", desc: "Help teams determine the most relevant action for each customer.", icon: "Target", color: "#FB8B53", bg: "#FFEFDE" },
      { title: "Experience Personalization", desc: "Create more relevant experiences based on customer context and behavior.", icon: "Zap", color: "#9333EA", bg: "#F3E8FF" },
    ],
  },
  {
    key: "productivity",
    label: "Increase Productivity",
    sub: "Help people do more",
    icon: "Settings",
    color: "#EA580C",
    bg: "#FFF7ED",
    border: "#FED7AA",
    headline: "Give your people more time for meaningful work.",
    desc: "Reduce repetitive effort, accelerate knowledge work and help employees complete complex tasks faster with AI.",
    img: "obj_3",
    examples: [
      { title: "Workplace Copilot", desc: "Help employees complete everyday tasks faster with contextual AI assistance.", icon: "Target", color: "#7C67FC", bg: "#F2F0FE" },
      { title: "Knowledge at Work", desc: "Give employees instant access to the information and expertise they need.", icon: "Send", color: "#059669", bg: "#D8FFEB" },
      { title: "Meeting-to-Action Intelligence", desc: "Turn conversations into decisions, actions and follow-ups automatically.", icon: "Users", color: "#FB8B53", bg: "#FFEFDE" },
      { title: "Work Automation", desc: "Remove repetitive steps from everyday business processes.", icon: "Lightbulb", color: "#9333EA", bg: "#F3E8FF" },
    ],
  },
  {
    key: "costs",
    label: "Reduce Costs",
    sub: "Work smarter, spend less",
    icon: "DollarSign",
    color: "#D97706",
    bg: "#FFFBEB",
    border: "#FDE68A",
    headline: "Find the work that doesn't need to be done manually.",
    desc: "Identify unnecessary effort, eliminate process inefficiencies and automate repetitive work across the organization.",
    img: "obj_4",
    examples: [
      { title: "Process Efficiency Intelligence", desc: "Identify where time, effort and resources are being lost across business processes.", icon: "Clock", color: "#7C67FC", bg: "#F2F0FE" },
      { title: "Intelligent Process Automation", desc: "Automate repetitive work that currently requires manual intervention.", icon: "FileText", color: "#059669", bg: "#D8FFEB" },
      { title: "Resource Optimization", desc: "Use AI to improve allocation of people, capacity and operational resources.", icon: "Box", color: "#FB8B53", bg: "#FFEFDE" },
      { title: "Cost Driver Intelligence", desc: "Understand the activities and patterns driving unnecessary business costs.", icon: "Database", color: "#9333EA", bg: "#F3E8FF" },
    ],
  },
  {
    key: "decision",
    label: "Improve Decision Making",
    sub: "Turn data into decisions",
    icon: "UserCheck",
    color: "#9333EA",
    bg: "#FAF5FF",
    border: "#E9D5FF",
    headline: "Give every decision the context it deserves.",
    desc: "Bring together data, knowledge and business context so leaders and teams can understand situations faster and make more informed decisions.",
    img: "obj_5",
    examples: [
      { title: "Decision Intelligence", desc: "Bring together relevant information to help teams evaluate complex business decisions.", icon: "Zap", color: "#7C67FC", bg: "#F2F0FE" },
      { title: "Scenario Analysis", desc: "Explore different possible outcomes before making important decisions.", icon: "Search", color: "#059669", bg: "#D8FFEB" },
      { title: "Executive Intelligence", desc: "Give leaders a contextual view of performance, opportunities and emerging issues.", icon: "UserCheck", color: "#FB8B53", bg: "#FFEFDE" },
      { title: "Business Question Assistant", desc: "Ask questions across business information and receive contextual answers and insights.", icon: "HelpCircle", color: "#9333EA", bg: "#F3E8FF" },
    ],
  },
  {
    key: "risk",
    label: "Reduce Risk",
    sub: "See risks earlier",
    icon: "Monitor",
    color: "#3B82F6",
    bg: "#EFF6FF",
    border: "#BFDBFE",
    headline: "Move from reacting to anticipating.",
    desc: "Use AI to identify patterns, exceptions and emerging signals that can help your teams recognize potential risks earlier.",
    img: "obj_6",
    examples: [
      { title: "Emerging Risk Intelligence", desc: "Identify patterns and signals that could indicate developing business risks.", icon: "Settings", color: "#7C67FC", bg: "#F2F0FE" },
      { title: "Anomaly Intelligence", desc: "Detect unusual activity across processes, transactions and business operations.", icon: "Search", color: "#059669", bg: "#D8FFEB" },
      { title: "Compliance Intelligence", desc: "Monitor complex requirements and make relevant policies and information easier to navigate.", icon: "Monitor", color: "#FB8B53", bg: "#FFEFDE" },
      { title: "Contract Risk Intelligence", desc: "Identify important obligations, clauses and potential risks across contracts.", icon: "AlertCircle", color: "#9333EA", bg: "#F3E8FF" },
    ],
  },
  {
    key: "innovation",
    label: "Accelerate Innovation",
    sub: "Turn ideas into impact",
    icon: "Cpu",
    color: "#06B6D4",
    bg: "#ECFEFF",
    border: "#A5F3FC",
    headline: "Turn ideas into possibilities faster.",
    desc: "Use AI to explore new ideas, accelerate research, understand emerging opportunities and move from concept to experimentation faster.",
    img: "obj_7",
    examples: [
      { title: "Innovation Intelligence", desc: "Identify emerging technologies, customer needs and market opportunities.", icon: "BrainCircuit", color: "#7C67FC", bg: "#F2F0FE" },
      { title: "AI Research Partner", desc: "Accelerate research, synthesis and exploration of new ideas.", icon: "Search", color: "#059669", bg: "#D8FFEB" },
      { title: "Concept Exploration", desc: "Use AI to explore product, service and business-model possibilities.", icon: "Compass", color: "#FB8B53", bg: "#FFEFDE" },
      { title: "Rapid Prototyping", desc: "Move promising ideas from concept to working prototype faster.", icon: "Infinity", color: "#9333EA", bg: "#F3E8FF" },
    ],
  },
];

// Data: Industries
export const INDUSTRIES = [
  {
    key: "prof_services",
    label: "Professional Services",
    sub: "Deliver greater client value",
    icon: "Users",
    color: "#2563EB",
    bg: "#EFF6FF",
    border: "#BFDBFE",
    headline: "Deliver more value with intelligent expertise.",
    desc: "Use AI to accelerate research, improve knowledge work, automate repetitive tasks, and help your teams deliver better outcomes for every client.",
    img: "ind_1",
    examples: [
      {
        title: "AI Research Assistant",
        sub: "Accelerate research and analysis",
        desc: "Search across internal knowledge, documents and external sources to surface relevant insights faster.",
        icon: "Search",
        color: "#2563EB",
        bg: "#EFF6FF",
      },
      {
        title: "Proposal & RFP Intelligence",
        sub: "Create stronger proposals, faster",
        desc: "Analyze requirements, retrieve relevant expertise and help teams create tailored responses.",
        icon: "FileText",
        color: "#9333EA",
        bg: "#F3E8FF",
      },
      {
        title: "Knowledge Copilot",
        sub: "Turn expertise into accessible intelligence",
        desc: "Give teams instant access to methodologies, past projects, documents and institutional knowledge.",
        icon: "Lightbulb",
        color: "#10B981",
        bg: "#ECFDF5",
      },
    ],
  },
  {
    key: "manufacturing",
    label: "Manufacturing",
    sub: "Smarter, resilient operations",
    icon: "Factory",
    color: "#EA580C",
    bg: "#FFEDD5",
    border: "#FED7AA",
    headline: "Build smarter operations with intelligent manufacturing.",
    desc: "Use AI to improve production, optimize operations, predict equipment needs, and help teams make faster, more informed decisions across the factory.",
    img: "ind_2",
    examples: [
      {
        title: "Predictive Maintenance",
        sub: "Prevent downtime before it happens",
        desc: "Use equipment data and AI to identify potential failures and optimize maintenance schedules.",
        icon: "ShieldCheck",
        color: "#2563EB",
        bg: "#EFF6FF",
      },
      {
        title: "Quality Intelligence",
        sub: "Improve quality with intelligent inspection",
        desc: "Analyze production data and inspection information to identify defects, patterns and quality issues earlier.",
        icon: "Search",
        color: "#9333EA",
        bg: "#F3E8FF",
      },
      {
        title: "Production Copilot",
        sub: "Give teams intelligent operational support",
        desc: "Help production teams access information, troubleshoot issues and make faster operational decisions.",
        icon: "Box",
        color: "#10B981",
        bg: "#ECFDF5",
      },
    ],
  },
  {
    key: "healthcare",
    label: "Healthcare",
    sub: "Better care, stronger outcomes",
    icon: "HeartPulse",
    color: "#DB2777",
    bg: "#FCE7F3",
    border: "#FBCFE8",
    headline: "Deliver better care with intelligent healthcare.",
    desc: "Use AI to improve clinical and operational workflows, make knowledge more accessible, reduce administrative work, and help healthcare teams make better-informed decisions.",
    img: "ind_3",
    examples: [
      {
        title: "Clinical Knowledge Assistant",
        sub: "Give clinicians faster access to relevant knowledge",
        desc: "Search medical knowledge, guidelines, documents and internal information to surface relevant insights when needed.",
        icon: "FileText",
        color: "#2563EB",
        bg: "#EFF6FF",
      },
      {
        title: "Patient Experience Copilot",
        sub: "Make patient interactions more connected",
        desc: "Help teams understand patient needs, access relevant information and support more personalized interactions.",
        icon: "Users",
        color: "#9333EA",
        bg: "#F3E8FF",
      },
      {
        title: "Healthcare Operations Copilot",
        sub: "Make healthcare operations flow better",
        desc: "Help teams coordinate workflows, identify bottlenecks and reduce repetitive administrative work.",
        icon: "Activity",
        color: "#10B981",
        bg: "#ECFDF5",
      },
    ],
  },
  {
    key: "financial_services",
    label: "Financial Services",
    sub: "Intelligent financial decisions",
    icon: "BarChart2",
    color: "#059669",
    bg: "#D1FAE5",
    border: "#A7F3D0",
    headline: "Make financial services more intelligent.",
    desc: "Use AI to improve customer experiences, accelerate financial analysis, automate operational work, and help teams make faster, more informed decisions.",
    img: "ind_4",
    examples: [
      {
        title: "Financial Research Assistant",
        sub: "Accelerate financial research and analysis",
        desc: "Search financial information, reports, documents and internal knowledge to surface relevant insights faster.",
        icon: "Search",
        color: "#2563EB",
        bg: "#EFF6FF",
        link: "/cognitive/industries/financial-services",
      },
      {
        title: "Relationship Manager Copilot",
        sub: "Give teams better customer intelligence",
        desc: "Bring together customer information, interactions and relevant financial knowledge to support better conversations and decisions.",
        icon: "Users",
        color: "#9333EA",
        bg: "#F3E8FF",
      },
      {
        title: "Risk Intelligence",
        sub: "Identify risk signals earlier",
        desc: "Analyze relevant data and information to help teams identify patterns, exceptions and potential risk areas.",
        icon: "Shield",
        color: "#10B981",
        bg: "#ECFDF5",
      },
    ],
  },
  {
    key: "education",
    label: "Education",
    sub: "Unlock new learning possibilities",
    icon: "GraduationCap",
    color: "#9333EA",
    bg: "#F3E8FF",
    border: "#E9D5FF",
    headline: "Create more intelligent learning experiences.",
    desc: "Use AI to personalize learning, make institutional knowledge more accessible, reduce administrative work, and help educators and students achieve better outcomes.",
    img: "ind_5",
    examples: [
      {
        title: "Learning Copilot",
        sub: "Give every learner an intelligent assistant",
        desc: "Help students understand concepts, find relevant resources and receive personalized learning support.",
        icon: "Brain",
        color: "#2563EB",
        bg: "#EFF6FF",
      },
      {
        title: "Educator Copilot",
        sub: "Give educators more time for teaching",
        desc: "Help educators prepare content, analyze learning information and access relevant institutional knowledge.",
        icon: "GraduationCap",
        color: "#9333EA",
        bg: "#F3E8FF",
      },
      {
        title: "Institutional Knowledge Assistant",
        sub: "Make institutional knowledge easier to access",
        desc: "Connect policies, documents, courses, research and institutional knowledge into an intelligent knowledge experience.",
        icon: "Bot",
        color: "#10B981",
        bg: "#ECFDF5",
      },
    ],
  },
  {
    key: "retail",
    label: "Retail & Consumer",
    sub: "More meaningful experiences",
    icon: "ShoppingCart",
    color: "#D97706",
    bg: "#FEF3C7",
    border: "#FDE68A",
    headline: "Create more intelligent customer experiences.",
    desc: "Use AI to understand customers, personalize experiences, optimize operations, improve demand planning, and help teams make faster decisions.",
    img: "ind_6",
    examples: [
      {
        title: "Customer Intelligence",
        sub: "Understand what customers need next",
        desc: "Bring together customer signals, interactions and behavior to help teams understand needs and opportunities.",
        icon: "User",
        color: "#2563EB",
        bg: "#EFF6FF",
      },
      {
        title: "Personalization Engine",
        sub: "Create more relevant customer experiences",
        desc: "Use AI to deliver more relevant product, content and experience recommendations.",
        icon: "Cpu",
        color: "#9333EA",
        bg: "#F3E8FF",
      },
      {
        title: "Retail Operations Copilot",
        sub: "Help teams run operations more intelligently",
        desc: "Give teams access to operational insights, workflows and recommendations to improve day-to-day execution.",
        icon: "Settings",
        color: "#10B981",
        bg: "#ECFDF5",
      },
    ],
  },
  {
    key: "social_impact",
    label: "Social Impact",
    sub: "Greater community outcomes",
    icon: "Users",
    color: "#A855F7",
    bg: "#F5EEFD",
    border: "#E9D5FF",
    headline: "Scale impact with intelligent solutions.",
    desc: "Use AI to make knowledge more accessible, improve program delivery, understand communities, and help teams create greater impact with limited resources.",
    img: "ind_7",
    examples: [
      {
        title: "Impact Intelligence",
        sub: "Turn program data into actionable insight",
        desc: "Analyze program and outcome information to identify patterns, opportunities and areas for improvement.",
        icon: "Search",
        color: "#2563EB",
        bg: "#EFF6FF",
      },
      {
        title: "Knowledge & Research Assistant",
        sub: "Make critical knowledge easier to access",
        desc: "Help teams find relevant research, documents, policies and institutional knowledge faster.",
        icon: "Brain",
        color: "#9333EA",
        bg: "#F3E8FF",
      },
      {
        title: "Program Delivery Copilot",
        sub: "Help teams deliver programs more effectively",
        desc: "Support teams with information, workflows, documentation and operational decision-making.",
        icon: "Bot",
        color: "#10B981",
        bg: "#ECFDF5",
      },
    ],
  },
  {
    key: "government",
    label: "Government & Public Services",
    sub: "More efficient public services",
    icon: "Landmark",
    color: "#0D9488",
    bg: "#CCFBF1",
    border: "#99F6E4",
    headline: "Deliver better public services with intelligent government.",
    desc: "Use AI to improve citizen experiences, make government knowledge more accessible, reduce administrative work, and help teams deliver services more effectively.",
    img: "ind_8",
    examples: [
      {
        title: "Citizen Services Assistant",
        sub: "Make public services easier to navigate",
        desc: "Help citizens find relevant information, understand processes and access services more easily.",
        icon: "Search",
        color: "#2563EB",
        bg: "#EFF6FF",
      },
      {
        title: "Government Knowledge Copilot",
        sub: "Make public-sector knowledge accessible",
        desc: "Connect policies, regulations, documents and institutional knowledge so teams can find relevant information faster.",
        icon: "Brain",
        color: "#9333EA",
        bg: "#F3E8FF",
      },
      {
        title: "Policy Intelligence",
        sub: "Turn complex information into actionable insight",
        desc: "Help teams analyze documents, research and data to understand policy questions and support informed decision-making.",
        icon: "Bot",
        color: "#10B981",
        bg: "#ECFDF5",
      },
    ],
  },
];

const OppExplorer = ({ activeTab = "Industries", selectedIndustry, selectedFunction }) => {
  const [active, setActive] = useState(0); 

  // Read the activeTab prop to decide which array to render
  const activeData =
    activeTab === "Business Function"
      ? FUNCTIONS
      : activeTab === "Industries"
      ? INDUSTRIES
      : OBJECTIVES;

  // Update active item whenever activeTab, selectedIndustry, or selectedFunction changes
  useEffect(() => {
    if (activeTab === "Industries" && selectedIndustry) {
      const q = selectedIndustry.toString().toLowerCase().trim();
      const idx = INDUSTRIES.findIndex(
        (item) =>
          item.key.toLowerCase() === q ||
          item.label.toLowerCase() === q ||
          item.label.toLowerCase().includes(q) ||
          q.includes(item.label.toLowerCase()) ||
          (q.includes("government") && (item.key === "government" || item.label.toLowerCase().includes("government"))) ||
          (q.includes("retail") && (item.key === "retail" || item.label.toLowerCase().includes("retail"))) ||
          (q.includes("financial") && (item.key === "financial_services" || item.label.toLowerCase().includes("financial"))) ||
          (q.includes("social") && (item.key === "social_impact" || item.label.toLowerCase().includes("social"))) ||
          (q.includes("prof") && (item.key === "prof_services" || item.label.toLowerCase().includes("prof")))
      );
      if (idx !== -1) {
        setActive(idx);
        return;
      }
    }

    if (activeTab === "Business Function" && selectedFunction) {
      const q = selectedFunction.toString().toLowerCase().trim();
      const idx = FUNCTIONS.findIndex(
        (item) =>
          item.key.toLowerCase() === q ||
          item.label.toLowerCase() === q ||
          item.label.toLowerCase().includes(q) ||
          q.includes(item.key.toLowerCase()) ||
          (q.includes("revenue") && item.key === "revenue") ||
          (q.includes("cx") && item.key === "cx") ||
          (q.includes("customer") && item.key === "cx") ||
          (q.includes("finance") && item.key === "finance") ||
          (q.includes("ops") && item.key === "ops") ||
          (q.includes("operations") && item.key === "ops") ||
          (q.includes("people") && item.key === "people") ||
          (q.includes("knowledge") && item.key === "knowledge")
      );
      if (idx !== -1) {
        setActive(idx);
        return;
      }
    }

    setActive(0);
  }, [activeTab, selectedIndustry, selectedFunction]);

  const fn = activeData[active] || activeData[0];

  return (
    <section id="explorer" className="w-full bg-[#F8FAFC] pb-16 pt-2">
      
      {/* Internal CSS for smooth tab transitions */}
      <style>{`
        @keyframes tabFadeSlide {
          0% { opacity: 0; transform: translateY(6px); }
          100% { opacity: 1; transform: translateY(0); }
        }
        .animate-tab {
          animation: tabFadeSlide 0.3s cubic-bezier(0.4, 0, 0.2, 1) forwards;
        }
      `}</style>

      <div className="mx-auto max-w-[1360px] px-6 lg:px-8">
        {/* Main card matching Figma screenshot */}
        <div className="grid min-h-[680px] grid-cols-1 overflow-hidden rounded-[24px] border border-[#E2E8F0] bg-white shadow-[0_4px_24px_rgba(15,23,42,0.04)] lg:grid-cols-[290px_1fr]">
          
          {/* Sidebar Navigation */}
          <div className="flex flex-col gap-1 border-b border-[#E2E8F0] p-4 lg:border-b-0 lg:border-r">
            {activeData.map((f, i) => {
              const on = i === active;
              return (
                <button
                  key={f.key}
                  onClick={() => setActive(i)}
                  className={`group flex items-center gap-3 rounded-[14px] px-3.5 py-3 text-left transition-all cursor-pointer ${
                    on
                      ? "bg-[#EFF6FF] text-[#1E40AF]"
                      : "hover:bg-[#F8FAFC] text-[#334155]"
                  }`}
                >
                  <span 
                    className="flex h-[36px] w-[36px] shrink-0 items-center justify-center rounded-[10px] transition-colors" 
                    style={{ backgroundColor: on ? "#DBEAFE" : f.bg }}
                  >
                    <Ico name={f.icon} className="h-[18px] w-[18px]" strokeWidth={2.2} style={{ color: on ? "#2563EB" : f.color }} />
                  </span>
                  <span className="flex flex-1 flex-col overflow-hidden">
                    <span className={`text-[14px] font-bold truncate ${on ? "text-[#1E40AF]" : "text-[#0F172A]"}`}>
                      {f.label}
                    </span>
                    <span className={`text-[12px] truncate ${on ? "text-[#3B82F6]" : "text-[#94A3B8]"}`}>
                      {f.sub}
                    </span>
                  </span>
                  <ChevronRight className="h-4 w-4 shrink-0 transition-transform duration-200" style={{ color: on ? "#2563EB" : "#CBD5E1" }} />
                </button>
              );
            })}
          </div>

          {/* Main Content Panel - "key" triggers the animation on tab/item change */}
          <div key={`${activeTab}-${active}`} className="animate-tab flex h-full flex-col justify-between gap-8 p-8 lg:p-10">
            
            {/* Header Area */}
            <div className="flex flex-col gap-6 lg:flex-row lg:items-center lg:justify-between">
              
              {/* Text Block */}
              <div className="flex w-full max-w-[480px] flex-col justify-start gap-3">
                <p className="text-[11px] font-bold uppercase tracking-[2px] text-[#94A3B8]">{fn.label}</p>
                <h2 className="text-[32px] sm:text-[36px] font-bold leading-[1.18] tracking-tight text-[#0F172A]">{fn.headline}</h2>
                <p className="text-[14.5px] sm:text-[15px] leading-[24px] text-[#475569]">{fn.desc}</p>
              </div>
              
              {/* Image Block */}
              <div className="relative flex h-[280px] sm:h-[320px] w-full max-w-[460px] items-center justify-center">
                <img 
                  src={`/images/opportunities/${fn.img}.png`} 
                  alt={fn.label} 
                  className="max-h-full w-full object-contain" 
                />
              </div>
            </div>

            {/* Example Cards Grid */}
            <div className="mt-auto flex flex-col gap-3.5 pt-4">
              <p className="text-[11px] font-bold uppercase tracking-[2px] text-[#94A3B8]">EXAMPLE OPPORTUNITIES</p>
              <div className={`grid grid-cols-1 gap-4 sm:grid-cols-2 ${fn.examples.length === 3 ? "lg:grid-cols-3" : "lg:grid-cols-4"}`}>
                {fn.examples.map((ex) => {
                  const targetLink = ex.link || (ex.title === "Financial Research Assistant" ? "/cognitive/industries/financial-services" : ex.title === "Relationship Manager Copilot" ? "/cognitive/industries/financial-services/relationship-manager-copilot" : ex.title === "Knowledge Copilot" ? "/cognitive/industries/professional-services/knowledge-copilot" : ex.title === "Predictive Maintenance" ? "/cognitive/industries/manufacturing/predictive-maintenance" : ex.title === "Production Copilot" ? "/cognitive/industries/manufacturing/production-copilot" : ex.title === "Quality Intelligence" ? "/cognitive/industries/manufacturing/quality-intelligence" : ex.title === "Clinical Knowledge Assistant" ? "/cognitive/industries/healthcare/clinical-knowledge-assistant" : ex.title === "Healthcare Operations Copilot" ? "/cognitive/industries/healthcare/healthcare-operations-copilot" : ex.title === "Patient Experience Copilot" ? "/cognitive/industries/healthcare/patient-experience-copilot" : ex.title === "AI Research Assistant" ? "/cognitive/industries/professional-services/ai-research-assistant" : ex.title === "Proposal & RFP Intelligence" ? "/cognitive/industries/professional-services/proposal-rfp-intelligence" : ex.title === "Citizen Services Assistant" ? "/cognitive/industries/government-public-services/citizen-services-assistant" : ex.title === "Government Knowledge Copilot" ? "/cognitive/industries/government-public-services/government-knowledge-copilot" : null);

                  const content = (
                    <>
                      <div className="flex items-center justify-between w-full">
                        <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-[10px]" style={{ background: ex.bg }}>
                          <Ico name={ex.icon} className="h-4 w-4" strokeWidth={2.2} style={{ color: ex.color }} />
                        </span>
                        {targetLink && (
                          <span className="text-[11.5px] font-semibold text-[#007AFF] opacity-0 group-hover:opacity-100 transition-opacity flex items-center gap-0.5">
                            <span>Explore</span>
                            <ChevronRight className="h-3.5 w-3.5" />
                          </span>
                        )}
                      </div>
                      <h3 className={`mt-1 text-[15px] font-bold leading-snug text-[#0F172A] ${targetLink ? "group-hover:text-[#007AFF] transition-colors" : ""}`}>
                        {ex.title}
                      </h3>
                      {ex.sub && <p className="text-[12px] font-medium text-[#64748B]">{ex.sub}</p>}
                      <p className="text-[12.5px] leading-[19px] text-[#64748B]">{ex.desc}</p>
                    </>
                  );

                  if (targetLink) {
                    return (
                      <Link
                        key={ex.title}
                        to={targetLink}
                        className="group flex min-h-[190px] flex-col justify-start gap-2.5 rounded-[16px] border border-[#E2E8F0] bg-white p-5 shadow-sm transition-all duration-200 hover:-translate-y-0.5 hover:shadow-md cursor-pointer hover:border-[#00D2EE]/60 hover:shadow-[0_4px_20px_rgba(0,210,238,0.15)] select-none text-left"
                      >
                        {content}
                      </Link>
                    );
                  }

                  return (
                    <div key={ex.title} className="flex min-h-[190px] flex-col justify-start gap-2.5 rounded-[16px] border border-[#E2E8F0] bg-white p-5 shadow-sm transition-all duration-200 hover:-translate-y-0.5 hover:shadow-md">
                      {content}
                    </div>
                  );
                })}
              </div>
            </div>

          </div>
        </div>
      </div>
    </section>
  );
};

export default OppExplorer;